import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppContext from '../../contexts/AppContext';
import DataContext from '../../contexts/DataContext';
import Button from '../../components/Button';
import CustomSelect from '../../components/CustomSelect';

import Api from '../api';
import {
  getCurrentInstallerData,
  type InstallerData,
} from '../components/Installer';
import Slide from '../components/Slide';
import { batteryInstallationSite as batteryInstallationSitePageData } from '../data/pages';
import { Page } from '../enums';
import {
  type Site,
  type SiteOption,
  type SiteDetailsRequest,
  type SiteDetailsResponse,
} from '../types';

const getSiteOption = (site: Site): SiteOption => {
  return {
    country: site.country,
    firstName: site.first_name,
    installerId: site.installer_id,
    label: site.site_name,
    lastName: site.last_name,
    maintainerId: site.maintainer_id,
    siteId: site.site_id,
    siteName: site.site_name,
    value: site.site_id,
    zipCode: site.zip_code,
  };
};

const getInitialSiteOption = (): SiteOption => {
  return {
    country: '',
    firstName: '',
    installerId: -1,
    label: 'Site not available',
    lastName: '',
    maintainerId: -1,
    siteId: -1,
    siteName: '',
    value: -1,
    zipCode: '',
  };
};

export default function BatteryInstallationSite() {
  const {
    currentPageIndex,
    setCurrentPageIndex,
    enlightenData,
    setEnlightenData,
  } = useContext(AppContext);

  const { data, updateData } = useContext(DataContext);

  const [selectedOption, setSelectedOption] = useState<SiteOption>(
    getInitialSiteOption()
  );
  const [hasError, setHasError] = useState(false);
  const [isLoadingSiteDetails, setIsLoadingSiteDetails] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const { heading, placeholder } = batteryInstallationSitePageData;
  const { sites, token } = enlightenData;

  const goToPage = (page: Page) => {
    // set current page index
    setCurrentPageIndex(currentPageIndex + 1);

    // update page param and navigate to next page
    searchParams.set('page', page);
    setSearchParams(searchParams);
  };

  const handleChange = (selectedOption: SiteOption) => {
    setSelectedOption(selectedOption);
  };

  const siteNotAvailableOption: SiteOption = getInitialSiteOption();
  const siteOptions =
    sites.length > 0
      ? sites.map((site: Site) => getSiteOption(site))
      : [siteNotAvailableOption];

  const handleSubmit = () => {
    const isSiteNotAvailableSelected =
      selectedOption?.value === siteNotAvailableOption.value;
    setHasError(false);

    if (isSiteNotAvailableSelected) {
      goToPage(Page.TechnicalSupport);
    } else {
      const { siteId, zipCode } = selectedOption;

      setEnlightenData({
        ...enlightenData,
        selectedSite: selectedOption,
      });

      setIsLoadingSiteDetails(true);

      const request: SiteDetailsRequest = {
        siteId,
        token,
        zipCode: zipCode || data.selectedZipCode,
      };
      Api.getSiteDetails(request).then(
        (response: SiteDetailsResponse) => {
          setIsLoadingSiteDetails(false);
          const {
            site_meta_data_status: isSiteMetadataAvailable,
            matching_zip_status: isMatchingZipAvailable,
          } = response;

          if (isSiteMetadataAvailable) {
            const { site_meta_data: siteMetadata } = response;

            if (siteMetadata) {
              const { micro_compatible: isMicroCompatible } = siteMetadata;
              const siteProfile = response.site_profile.find(
                (profile) => profile.site_id === siteId
              );

              let currentInstallerData: InstallerData | null = null;
              if (isMatchingZipAvailable) {
                const { matching_zip: matchingZip } = response;
                currentInstallerData = getCurrentInstallerData(
                  matchingZip!.currentInstaller
                );
              }

              setSelectedOption(selectedOption);

              setEnlightenData({
                ...enlightenData,
                selectedSite: {
                  ...selectedOption,
                  siteMetadata,
                  currentInstaller: currentInstallerData,
                },
              });

              updateData({
                ...data,
                selectedAddress: siteProfile?.address1,
                selectedCity: siteProfile?.city,
                selectedState: siteProfile?.state,
                selectedStateName: siteProfile?.state,
                selectedZipCode: siteProfile?.zip,
              });

              if (isMicroCompatible) {
                goToPage(Page.EnergyConsumption);
              } else {
                goToPage(Page.UpgradeMicroinverters);
              }
            }
          } else {
            setHasError(true);
            setIsLoadingSiteDetails(false);
          }
        },
        (error) => {
          setHasError(true);
          setIsLoadingSiteDetails(false);
          console.error('Error loading site details:', error);
        }
      );
    }
  };

  useEffect(() => {
    if (sites.length > 0) {
      const isSiteSelected =
        enlightenData?.selectedSite &&
        enlightenData?.selectedSite?.value !== siteNotAvailableOption.value;

      if (isSiteSelected) {
        setSelectedOption(enlightenData?.selectedSite);
      } else {
        const firstSite = sites[0];
        const firstSiteOption = getSiteOption(firstSite);
        setSelectedOption(firstSiteOption);
      }
    } else {
      setSelectedOption(siteNotAvailableOption);
    }
  }, [sites]);

  return (
    <>
      <Slide heading={heading} />
      <div
        className={`w-full max-w-[294px] md:max-w-[400px] md-max:max-w-[279px] short:!w-[350px] h-10 md:h-[57px] my-8 text-[14px]`}>
        <CustomSelect
          onChange={handleChange}
          options={siteOptions}
          placeholder={placeholder}
          value={selectedOption}
          zukoId="battery-installation-site-select"
        />
        {hasError && (
          <p className="text-sm text-red-600 mt-2">
            Oops, something went wrong. Please try selecting another site.
          </p>
        )}
      </div>
      <div className="mt-[50px] md:mt-[60px]">
        <Button
          action={handleSubmit}
          disabled={!selectedOption}
          isLoading={isLoadingSiteDetails}
         // data-zuko-id="battery-installation-site_continue"
        />
      </div>
    </>
  );
}
